import { z } from 'zod';
import { TIME_STYLES } from './constants';

export const ColumnPinningPosition = z.union([
  z.literal('left'),
  z.literal('right'),
  z.literal(false),
]);
const OptionalDateTime = z.string().datetime().optional();

const currencySeparator = z.enum(['comma', 'period', 'space', 'apostrophe']);

const symbolPlacement = z.enum(['before', 'after', 'hidden']);

export const tagStructure = z.enum(['consolidated', 'separate']);

export const tagDisplay = z.enum(['tags', 'tagLists', 'listsAndTags']);

const aggregateFunctionName = z.enum([
  'sum',
  'average',
  'median',
  'count',
  'countPercentage',
  'uniqueCount',
  'emptyLabel',
]);

const DatatableSetting = <T extends z.ZodTypeAny>(value: T) =>
  z
    .object({
      value,
      /**
       * * When updated, the user's persisted setting will be replaced by
       * the current default value.
       * (same thing happens when the property is introduced for the first time)
       * * ISO 8601 Datetime (e.g.: `2023-10-01T00:00:00Z`)
       */
      updatedAt: OptionalDateTime,
    })
    .strict();

const DatatableSettings = z
  .object({
    zebraStripes: DatatableSetting(z.boolean()).optional(),
    verticalBorders: DatatableSetting(z.boolean()).optional(),
    timeStyle: DatatableSetting(z.enum(TIME_STYLES)).optional(),
    currencyFormat: DatatableSetting(
      z.object({
        separator: z.string(),
        symbolPlacement,
      })
    ).optional(),
    tagStyle: DatatableSetting(
      z.object({
        tagStructure: tagStructure,
        tagDisplay: tagDisplay,
      })
    ).optional(),
    aggregateFunctions: DatatableSetting(z.record(z.string(), aggregateFunctionName)).optional(),
    breakdownType: DatatableSetting(z.enum(['day', 'week', 'month', 'quarter', 'year'])).optional(),
    interval_value: DatatableSetting(z.enum(['time', 'money', 'cost', 'profit'])).optional(),
  })
  .strict();

const ColumnState = z.object({
  id: z.string(),
  isVisible: z.boolean(),
  pinned: ColumnPinningPosition,
  /**
   * * When updated, the user's persisted column state will be replaced by
   * the current default value.
   * (same thing happens when the property is introduced for the first time)
   * * ISO 8601 Datetime (e.g.: `2023-10-01T00:00:00Z`)
   */
  updatedAt: OptionalDateTime,
});

const DatatableState = z
  .object({
    columns: z.array(ColumnState),
    /**
     * * When updated, the user's persisted column order will be replaced by
     * the current default order.
     * (same thing happens when the property is introduced for the first time)
     * * ISO 8601 Datetime (e.g.: `2023-10-01T00:00:00Z`)
     */
    columnOrderUpdatedAt: OptionalDateTime,
    settings: DatatableSettings.optional(),
  })
  .strict();

export const isValidDatatableState = (state: DatatableState) =>
  DatatableState.safeParse(state).success;

export type DatatableSettings = z.infer<typeof DatatableSettings>;
export type DatatableSettingsKey =
  | 'zebraStripes'
  | 'verticalBorders'
  | 'timeStyle'
  | 'currencyFormat'
  | 'tagStyle'
  | 'aggregateFunctions'
  | 'breakdownType'
  | 'interval_value';

export type BreakdownType = 'day' | 'week' | 'month' | 'quarter' | 'year';

export type IntervalValueType = 'time' | 'money' | 'cost' | 'profit';

export type DatatableSettingsState = Partial<{
  [Key in DatatableSettingsKey]: NonNullable<DatatableSettings[Key]>['value'];
}>;

export type ColumnState = z.infer<typeof ColumnState>;
export type TimeStyle = (typeof TIME_STYLES)[number];
export type CurrencySeparator = z.infer<typeof currencySeparator>;
/** Thousands separator – decimal separator */
export type CurrencySeparatorOption = `${CurrencySeparator}-${CurrencySeparator}`; // cannot be inferred by Zod
export type SymbolPlacement = z.infer<typeof symbolPlacement>;
export type CurrencyFormatOptions = {
  separator: CurrencySeparatorOption;
  symbolPlacement: SymbolPlacement;
};
export type AggregateFunctionName = z.infer<typeof aggregateFunctionName>;
export type DatatableState = z.infer<typeof DatatableState>;
export type TagStructure = z.infer<typeof tagStructure>;
export type TagDisplay = z.infer<typeof tagDisplay>;
export type TagStyle = {
  tagStructure: TagStructure;
  tagDisplay: TagDisplay;
};

declare module '@tanstack/react-table' {
  // biome-ignore lint/correctness/noUnusedVariables: intentional
  interface ColumnMeta<TData, TValue> {
    // Whether to completely hide the aggregate view in the footer
    excludeAggregateView?: boolean;
    // Whether to show the aggregate value but make it read-only (no dropdown menu)
    readOnlyAggregateView?: boolean;
    alignRight?: boolean;
    numeric?: boolean;
    mandatory?: boolean;
    time?: boolean;
    timeStyle?: string;
    label?: string;
    getValue?: (value: any) => any;
  }
}
